import type { ComponentProps, ReactNode } from 'react';
import { Avatar, HStack, Text, VStack } from '@chakra-ui/react';
import { PlayerProfileLink } from './PlayerProfileLink';
import { RiftboundPlayerAvatar } from './RiftboundPlayerAvatar';
import { SlugText } from './SlugText';

type AvatarSize = NonNullable<ComponentProps<typeof Avatar.Root>['size']>;

export interface PlayerIdentityProps {
  name: string;
  /** Profile handle. When present and `linked`, the row navigates to `/players/:slug`. */
  slug?: string | null;
  photoURL?: string | null;
  size?: AvatarSize;
  /** Shown after the display name, e.g. ` (you)`. */
  nameSuffix?: string;
  /** Role chip, lock icon, etc. next to the name. */
  trailing?: ReactNode;
  /** Wrap the avatar (team role frame, facepile ring). */
  avatarWrapper?: (avatar: ReactNode) => ReactNode;
  /** When false, still show the handle but do not nest a link (parent card is already linked). */
  linked?: boolean;
  /** Fallback handle text when `slug` is empty. */
  handleFallback?: string;
}

/**
 * Shared avatar + name + @handle row used on team rosters, invites, and requests.
 */
export function PlayerIdentity({
  name,
  slug,
  photoURL,
  size = 'sm',
  nameSuffix,
  trailing,
  avatarWrapper,
  linked = true,
  handleFallback,
}: PlayerIdentityProps) {
  const displayName = name.trim() || 'Player';
  const handle = slug?.trim() || handleFallback;
  const avatar = (
    <RiftboundPlayerAvatar name={displayName} photoURL={photoURL} size={size} flexShrink={0} />
  );

  const body = (
    <HStack gap={3} align="center" minW={0} flex={1}>
      {avatarWrapper ? avatarWrapper(avatar) : avatar}
      <VStack align="start" gap={0} flex={1} minW={0}>
        <HStack gap={2} flexWrap="wrap" align="center">
          <Text
            data-player-identity-name=""
            fontSize="sm"
            fontWeight="semibold"
            lineClamp={1}
          >
            {displayName}
            {nameSuffix ?? ''}
          </Text>
          {trailing}
        </HStack>
        {handle ? <SlugText slug={handle} fontSize="xs" color="textMuted" /> : null}
      </VStack>
    </HStack>
  );

  if (!linked) return body;

  return (
    <PlayerProfileLink slug={slug} variant="identity" label={`${displayName} profile`}>
      {body}
    </PlayerProfileLink>
  );
}
